/**
 * Classify a glyph selection and convert it to components or ccmp.
 * Conversion follows the composition already in the font. It does not
 * consult a language-pack recipe, so a sequence the user changed is kept.
 * Recipes are only for adding glyphs.
 */
import { parseConnName } from './conn-names';
import { applyCompositionPlan } from './composition-applicator';
import { applyForms, arabicFormRules, familyRoot } from './arabic-forms';
import { ccmpDecomposition, ccmpShellNames } from './ccmp-shells';
import { expandFormSequence, planArabicConversion, planExistingConversion } from './composition-planner';
export function classifyComposition(names, lookup) {
    const offer = {
        toCcmp: [],
        toComponents: [],
        outlinesLostByCcmp: [],
        outlinesLostByComponents: [],
        ccmpReason: null,
        componentsReason: null,
        kept: []
    };
    for (const name of names) {
        const glyph = lookup.glyph(name);
        const outlined = hasOutlines(glyph);
        if (lookup.isCcmp(name)) {
            if (lookup.decomposition(name).length) {
                offer.toComponents.push(name);
                if (outlined) {
                    offer.outlinesLostByComponents.push(name);
                }
            }
            continue;
        }
        if (lookup.components(name).length) {
            offer.toCcmp.push(name);
            if (outlined) {
                offer.outlinesLostByCcmp.push(name);
            }
        }
    }
    if (!offer.toCcmp.length) {
        offer.ccmpReason = names.every((name) => lookup.isCcmp(name))
            ? 'Already ccmp'
            : 'No components';
    }
    if (!offer.toComponents.length) {
        offer.componentsReason = names.some((name) => lookup.isCcmp(name))
            ? 'No components'
            : 'Not ccmp';
    }
    return offer;
}
export function conversionMenuLabel(target, count, total) {
    if (count > 0 && count < total) {
        return `Convert ${count} to ${target}`;
    }
    return `Convert to ${target}`;
}
export function glyphComponentNames(glyph) {
    const names = [];
    for (const layer of glyph?.layers || []) {
        for (const shape of layer.shapes || []) {
            const name = componentReference(shape);
            if (name && !names.includes(name)) {
                names.push(name);
            }
        }
        if (names.length) {
            return names;
        }
    }
    return names;
}
export async function loadCompositionOffer(names) {
    const font = window.currentFontModel;
    if (!font) {
        return classifyComposition(names, {
            glyph: () => undefined,
            isCcmp: () => false,
            components: () => [],
            decomposition: () => []
        });
    }
    const shells = ccmpShellNames(font.features);
    const rules = ccmpDecomposition(font.features);
    const roots = [
        ...new Set(names.map((name) => familyRoot(name, font) || name))
    ];
    const offer = classifyComposition(roots, {
        glyph: (name) => font.findGlyph(name),
        isCcmp: (name) => shells.has(name),
        components: (name) => glyphComponentNames(font.findGlyph(name)),
        decomposition: (name) => rules.get(name) || []
    });
    for (const root of roots) {
        if (!isFormFamily(font, root)) {
            continue;
        }
        const family = arabicFamily(font, root);
        if (family.missing) {
            offer.componentsReason = `Missing ${family.missing}`;
            offer.toCcmp = offer.toCcmp.filter((item) => item !== root);
            offer.toComponents = offer.toComponents.filter((item) => item !== root);
        }
        offer.kept.push(...family.pinned);
    }
    return offer;
}
export async function convertSelectedGlyphs(names, output) {
    const font = window.currentFontModel;
    if (!font || !names.length) {
        return [];
    }
    const rules = ccmpDecomposition(font.features);
    const roots = [
        ...new Set(names.map((name) => familyRoot(name, font) || name))
    ];
    const arabic = roots.filter((name) => isFormFamily(font, name));
    const latin = roots.filter((name) => !arabic.includes(name));
    const latinPlan = planExistingConversion(latin.map((name) => ({
        name,
        components: output === 'ccmp'
            ? glyphComponentNames(font.findGlyph(name))
            : rules.get(name) || []
    })), output);
    const arabicPlan = planArabicConversion(arabic.map((name) => arabicFamily(font, name)), output);
    const plan = mergePlans(latinPlan, arabicPlan);
    window.glyphCanvas?.textRunEditor?.holdSelectionForComposition(roots);
    applyCompositionPlan(font, plan, output === 'ccmp' ? 'Convert to ccmp' : 'Convert to Components');
    return plan.kept;
}
function isFormFamily(font, name) {
    const codepoint = font.findGlyph(name)?.codepoints?.[0];
    if (name.endsWith('-ar') ||
        (typeof codepoint === 'number' &&
            codepoint >= 0x0600 &&
            codepoint <= 0x08ff)) {
        return true;
    }
    return ['init', 'medi', 'fina'].some((position) => Boolean(font.findGlyph(`${name}.${position}`)));
}
function arabicFamily(font, name) {
    const formRules = arabicFormRules(font.features);
    const decomposition = ccmpDecomposition(font.features).get(name) || [];
    const isolComponents = glyphComponentNames(font.findGlyph(name));
    const isolRule = formRules.get('isol')?.get(name) || [];
    const isol = isolComponents.length
        ? isolComponents
        : decomposition.length
            ? decomposition
            : isolRule;
    const sequences = {};
    if (isol.length) {
        sequences.isol = isol;
    }
    const hasGlyph = (token) => Boolean(font.findGlyph(token));
    let missing = null;
    for (const position of ['init', 'medi', 'fina']) {
        const positionalName = `${name}.${position}`;
        const components = glyphComponentNames(font.findGlyph(positionalName));
        const rule = formRules.get(position)?.get(name) || [];
        if (components.length && font.findGlyph(positionalName)) {
            sequences[position] = components;
        }
        else if (rule.length) {
            sequences[position] = rule;
        }
        else if (sequences.isol) {
            const expanded = expandFormSequence(sequences.isol, position, hasGlyph);
            if (expanded.join(' ') !== sequences.isol.join(' ')) {
                sequences[position] = expanded;
            }
            else {
                const applied = applyForms(sequences.isol, position, formRules);
                if (applied.join(' ') !== sequences.isol.join(' ')) {
                    sequences[position] = applied;
                }
            }
        }
        if (sequences[position]?.some((token) => !hasGlyph(token)) &&
            !missing) {
            missing =
                sequences[position].find((token) => !hasGlyph(token)) || null;
        }
    }
    const members = ['init', 'medi', 'fina']
        .map((position) => `${name}.${position}`)
        .filter((positional) => hasGlyph(positional));
    const conn = connectionMembers(font, name, ccmpDecomposition(font.features));
    const chain = Boolean(sequences.isol) &&
        ['init', 'medi', 'fina'].every((position) => {
            if (!sequences[position]) {
                return true;
            }
            const target = sequences[position].join(' ');
            const expanded = expandFormSequence(sequences.isol, position, hasGlyph).join(' ');
            const applied = applyForms(sequences.isol, position, formRules).join(' ');
            return expanded === target || applied === target;
        });
    const pinned = [];
    for (const positional of members) {
        if (!font.preflightDeleteGlyphs) {
            continue;
        }
        const preflight = font.preflightDeleteGlyphs([positional]);
        const users = preflight.componentGlyphNames.filter((user) => user !== name);
        const foreignFeature = preflight.featureHits.some((hit) => {
            if (hit.kind !== 'feature') {
                return true;
            }
            return !(font.features?.features || []).some(([tag, code]) => {
                if (tag !== hit.name) {
                    return false;
                }
                const stamp = code.format_specific?.['com.counterpunch.generator'];
                return (stamp?.generator === 'space.counterpunch.ccmp' ||
                    stamp?.generator === 'space.counterpunch.arabic');
            });
        });
        if (users.length ||
            foreignFeature ||
            preflight.kerningPairReferences > 0 ||
            preflight.metricsKeyReferences > 0) {
            pinned.push(positional);
        }
    }
    return { name, sequences, chain, pinned, missing, members, conn };
}
function connectionMembers(font, familyName, rules) {
    const found = [];
    for (const glyph of font.glyphs || []) {
        const parsed = parseConnName(glyph.name);
        if (!parsed) {
            continue;
        }
        const root = familyRoot(parsed.source, font) || parsed.source;
        if (root !== familyName && parsed.source !== familyName) {
            continue;
        }
        const components = glyphComponentNames(font.findGlyph(glyph.name));
        const decomposition = rules.get(glyph.name) || [];
        const sequence = components.length ? components : decomposition;
        if (!sequence.length) {
            continue;
        }
        found.push({ name: glyph.name, components: sequence });
    }
    return found;
}
function mergePlans(latin, arabic) {
    return {
        create: [...latin.create, ...arabic.create],
        composites: [...latin.composites, ...arabic.composites],
        clearShells: [...latin.clearShells, ...arabic.clearShells],
        ccmpAdd: [...latin.ccmpAdd, ...arabic.ccmpAdd],
        ccmpRemove: [...latin.ccmpRemove, ...arabic.ccmpRemove],
        ccmpComponents: { ...latin.ccmpComponents, ...arabic.ccmpComponents },
        supportingCount: latin.supportingCount + arabic.supportingCount,
        skipped: [...latin.skipped, ...arabic.skipped],
        deletes: [...latin.deletes, ...arabic.deletes],
        kept: [...latin.kept, ...arabic.kept],
        arabicAdd: arabic.arabicAdd,
        arabicRemove: [...latin.arabicRemove, ...arabic.arabicRemove]
    };
}
function hasOutlines(glyph) {
    for (const layer of glyph?.layers || []) {
        for (const shape of layer.shapes || []) {
            if (isOutlineShape(shape)) {
                return true;
            }
        }
    }
    return false;
}
function isOutlineShape(shape) {
    if (!shape || typeof shape !== 'object') {
        return true;
    }
    const candidate = shape;
    if (typeof candidate.isComponent === 'function') {
        return !candidate.isComponent();
    }
    return typeof candidate.reference !== 'string';
}
function componentReference(shape) {
    if (!shape || typeof shape !== 'object') {
        return null;
    }
    const candidate = shape;
    if (typeof candidate.isComponent === 'function') {
        if (!candidate.isComponent() ||
            typeof candidate.asComponent !== 'function') {
            return null;
        }
        const reference = candidate.asComponent().reference;
        return typeof reference === 'string' && reference ? reference : null;
    }
    return typeof candidate.reference === 'string' && candidate.reference
        ? candidate.reference
        : null;
}
